import React, { useEffect, useState, useMemo } from 'react';
import { Row, Col, Card, CardBody, Input } from 'reactstrap';
import { activateAuthLayout } from '../../../store/actions';
import { connect } from 'react-redux';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';
import SweetAlert from 'react-bootstrap-sweetalert';
import { useHistory } from 'react-router';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import { getQuoteBuilderURL, getLogoUrl, formatTimestamp } from '../../../helpers/common';
import ImageDropzone from '../../../components/ImageDropzone';
import QuoteSettingsPanel from '../../../components/QuoteSettingsPanel';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { useOktaAuth } from '@okta/okta-react';

const QUOTE = gql`
  query getQuote($QuoteId: ID) {
    Quote(QuoteId: $QuoteId) {
      QuoteId
      PrimaryContact
      CompanyName
      ContactFirstname
      ContactSurname
      ContactEmail
      CustomerId
      Description
      CreatedBy
      Created
      ProposalTitle
      ValidUntil
      CustomerLogo
      DisplayCustomerLogo
      WorkflowStep
      AcceptedQuote
      Status
      ReferringAgentId
      ReferringAgent {
        Name
      }
      SalesAgent {
        id
        FirstName
        LastName
      }
      TeamId
      Team {
        Name
      }
      PartnerId
      Partner {
        Name
      }
      QuoteTypeId1
      QuoteTypeId2
      QuoteContractMonths1
      QuoteContractMonths2
      GroupItemsByCategory
      OpportunityTicketKey
    }
  }
`;

const GET_USERS = gql`
  query Users {
    Users {
      id
      email
      FirstName
      LastName
      AgentCode
      TeamId
    }
  }
`;

const GET_TEAMS = gql`
  query Teams {
    Teams {
      TeamId
      Name
    }
  }
`;

const GET_AGENTS = gql`
  query Agents {
    Agents {
      AgentId
      OctaneId
      Name
    }
  }
`;

const GET_PARTNERS = gql`
  query Partners {
    Partners {
      PartnerId
      Name
    }
  }
`;

const QUOTE_TYPES = gql`
  query QuoteTypes {
    QuoteTypes {
      QuoteTypeId
      Name
      Description
      Active
    }
  }
`;

const QUOTE_ITEMS = gql`
  query QuoteItems($QuoteId: ID) {
    QuoteItems(QuoteId: $QuoteId) {
      LineId
      PriceType
      Product {
        ProductId
      }
    }
  }
`;

const UPLOAD_LOGO = gql`
  mutation uploadCustomerLogo($file: Upload!) {
    uploadCustomerLogo(file: $file)
  }
`;

const UPDATE_QUOTE = gql`
  mutation updateQuote($QuoteInput: QuoteInput) {
    updateQuote(input: $QuoteInput) {
      QuoteId
      PrimaryContact
      CompanyName
      ContactFirstname
      ContactSurname
      ContactEmail
      CustomerLogo
      QuoteTypeId1
      QuoteTypeId2
      QuoteContractMonths1
      QuoteContractMonths2
      Description
      WorkflowStep
      AcceptedQuote
      ProposalModified
      CustomerId
      Status
      ReferringAgentId
      SalesAgentId
      TeamId
    }
  }
`;

const CREATE_QUOTE = gql`
  mutation createQuote($CreateQuoteInput: CreateQuoteInput) {
    createQuote(input: $CreateQuoteInput) {
      status
      message
      quote {
        QuoteId
      }
    }
  }
`;

const CREATE_QUOTE_FROM_TEMPLATE = gql`
  mutation createQuoteFromTemplate($input: CreateQuoteFromTemplateInput) {
    createQuoteFromTemplate(input: $input) {
      status
      message
      quote {
        QuoteId
      }
    }
  }
`;

const QUOTE_PREFILL = gql`
  query QuotePrefill($Key: String!) {
    QuotePrefill(Key: $Key) {
      OpportunityTicketKey
      Description
      SalesAgentCode
      TeamName
      ReferringAgentOctaneId
      CustomerId
      CompanyName
      ContactFirstname
      ContactSurname
      ContactEmail
    }
  }
`;

const GET_QUOTE_TEMPLATES = gql`
  query getQuoteTemplates {
    QuoteTemplates {
      QuoteId
      ProposalTitle
      Description
      PartnerId
      Partner {
        Name
      }
      QuoteTypeId1
      QuoteTypeId2
      QuoteContractMonths1
      QuoteContractMonths2
      GroupItemsByCategory
    }
  }
`;

const QuoteBuilderSetup = ({ activateAuthLayout, ...props }) => {
  useEffect(() => { activateAuthLayout(); }, [activateAuthLayout]);

  let history = useHistory();
  const { authState } = useOktaAuth();
  const userEmail = authState?.idToken?.claims?.email;

  const quoteIDParam = history.location.pathname.split('/').pop();
  const isNewQuote = quoteIDParam === 'new-quote';
  const [stepError, setStepError] = useState(null);
  const [recalcModal, setRecalcModal] = useState(null);
  
  const [salesAgent, setSalesAgent] = useState(null);
  const [team, setTeam] = useState(null);
  const [referringAgent, setReferringAgent] = useState(null);
  const [partner, setPartner] = useState(null);

  const [quoteType1, setQuoteType1] = useState(isNewQuote ? 1 : null);
  const [quoteType2, setQuoteType2] = useState(isNewQuote ? 2 : null);
  const [contractMonths1, setContractMonths1] = useState(null);
  const [contractMonths2, setContractMonths2] = useState(null);
  const [quoteTypes, setQuoteTypes] = useState(null);

  const [usersList, setUsersList] = useState(null);
  const [teamsList, setTeamsList] = useState(null);
  const [agentsList, setAgentsList] = useState(null);
  const [partnersList, setPartnersList] = useState(null);

  const [logoFile, setLogoFile] = useState(null);
  const [customerLogo, setCustomerLogo] = useState(null);
  const [displayCustomerLogo, setDisplayCustomerLogo] = useState(false);
  const [groupItemsByCategory, setGroupItemsByCategory] = useState(false);
  const [quoteDescription, setQuoteDescription] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [contactFirstname, setContactFirstname] = useState('');
  const [contactSurname, setContactSurname] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  const [quoteProposalTitle, setQuoteProposalTitle] = useState(null);
  const [quoteValidUntil, setQuoteValidUntil] = useState(null);

  const [template, setTemplate] = useState(null);
  const [opportunityTicketKey, setOpportunityTicketKey] = useState(null);

  const opportunityKey = isNewQuote
    ? new URLSearchParams(history.location.search).get('opportunity')
    : null;

  const [prefillApplied, setPrefillApplied] = useState(!opportunityKey);

  const [updateQuote] = useMutation(UPDATE_QUOTE);
  const [uploadLogo] = useMutation(UPLOAD_LOGO);
  const [createQuote] = useMutation(CREATE_QUOTE);
  const [createQuoteFromTemplate] = useMutation(CREATE_QUOTE_FROM_TEMPLATE);
  const { data: prefillData, loading: prefillLoading } = useQuery(QUOTE_PREFILL, {
    variables: { Key: opportunityKey },
    skip: !opportunityKey,
  });
  const { data: templatesData } = useQuery(GET_QUOTE_TEMPLATES, { skip: !isNewQuote });
  const { data: quoteTypesData } = useQuery(QUOTE_TYPES);
  const { data: existingQuoteData } = useQuery(QUOTE, {
    variables: { QuoteId: quoteIDParam },
    skip: isNewQuote,
  });
  const { data: usersData } = useQuery(GET_USERS);
  const { data: teamsData } = useQuery(GET_TEAMS);
  const { data: agentsData } = useQuery(GET_AGENTS);
  const { data: partnersData } = useQuery(GET_PARTNERS);
  const { data: quoteItemsData } = useQuery(QUOTE_ITEMS, {
    variables: { QuoteId: quoteIDParam },
    skip: isNewQuote,
  });

  const hasQuoteItems = (quoteItemsData?.QuoteItems?.length ?? 0) > 0;

  const [disableQuote, setDisableQuote] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const dirty = (fn) => (...args) => { fn(...args); setIsDirty(true); };
  const { unblockRef, leavePrompt } = useUnsavedChanges(isDirty);

  useEffect(() => {
    if (existingQuoteData && existingQuoteData.Quote) {
      const quote = existingQuoteData.Quote;

      if (!['QUOTE_SETUP', 'CUSTOMER_ACCEPTED'].includes(quote.WorkflowStep)) {
        history.replace(getQuoteBuilderURL(quote.WorkflowStep, quote.QuoteId));
        return;
      }

      const status = quote.Status || 'in_progress';
      setDisableQuote(status === 'accepted');

      setQuoteDescription(quote.Description || '');

      setQuoteType1(quote.QuoteTypeId1 != null ? quote.QuoteTypeId1 : '');
      setQuoteType2(quote.QuoteTypeId2 != null ? quote.QuoteTypeId2 : '');
      setContractMonths1(quote.QuoteContractMonths1 ?? null);
      setContractMonths2(quote.QuoteContractMonths2 ?? null);
      setCustomerLogo(quote.CustomerLogo);
      setDisplayCustomerLogo(quote.DisplayCustomerLogo);
      setGroupItemsByCategory(!!quote.GroupItemsByCategory);
      setFieldValues(quote);

      setCustomerId(quote.CustomerId || '');
      setCompanyName(quote.CompanyName || '');
      setContactFirstname(quote.ContactFirstname || '');
      setContactSurname(quote.ContactSurname || '');
      setContactEmail(quote.ContactEmail || '');
      setQuoteProposalTitle(quote.ProposalTitle || '');
      setQuoteValidUntil(quote.ValidUntil || null);
      setOpportunityTicketKey(quote.OpportunityTicketKey || null);
    }
  }, [existingQuoteData, history]);

  useEffect(() => {
    if (!prefillData?.QuotePrefill || !usersList || !teamsList || !agentsList) return;
    const p = prefillData.QuotePrefill;

    if (p.CustomerId) setCustomerId(p.CustomerId);
    if (p.CompanyName) setCompanyName(p.CompanyName);
    if (p.ContactFirstname) setContactFirstname(p.ContactFirstname);
    if (p.ContactSurname) setContactSurname(p.ContactSurname);
    if (p.ContactEmail) setContactEmail(p.ContactEmail);
    if (p.Description) setQuoteDescription(p.Description);
    if (p.OpportunityTicketKey) setOpportunityTicketKey(p.OpportunityTicketKey);

    const agentMatch = usersList.find(u => u.AgentCode === p.SalesAgentCode);
    setSalesAgent(agentMatch ? { value: agentMatch.id, label: `${agentMatch.FirstName} ${agentMatch.LastName}` } : null);

    setTeam(teamOptions.find(o => o.label.toLowerCase() === p.TeamName?.toLowerCase()) ?? null);

    const referringAgentMatch = agentsList.find(a => String(a.OctaneId) === String(p.ReferringAgentOctaneId));
    setReferringAgent(referringAgentMatch ? { value: referringAgentMatch.AgentId, label: referringAgentMatch.Name } : null);

    setPrefillApplied(true);
  }, [prefillData, usersList, teamsList, agentsList]);

  useEffect(() => {
    if (quoteTypesData) {
      setQuoteTypes(quoteTypesData.QuoteTypes.filter(t => t.QuoteTypeId !== 0));
    }
  }, [quoteTypesData]);

  useEffect(() => {
    if (teamsData) setTeamsList(teamsData.Teams);
  }, [teamsData]);

  useEffect(() => {
    if (agentsData) setAgentsList(agentsData.Agents);
  }, [agentsData]);

  useEffect(() => {
    if (partnersData) setPartnersList(partnersData.Partners);
  }, [partnersData]);

  const teamOptions = useMemo(() => (teamsList ?? []).map(t => ({ value: t.TeamId, label: t.Name })), [teamsList]);
  const partnerOptions = useMemo(() => (partnersList ?? []).map(p => ({ value: p.PartnerId, label: p.Name })), [partnersList]);

  useEffect(() => {
    if (!usersData) return;
    setUsersList(usersData.Users.filter(u => u.AgentCode));
  }, [usersData]);

  useEffect(() => {
    if (!isNewQuote || opportunityKey || salesAgent || !userEmail || !usersList || !teamsList) return;
    const match = usersList.find(u => u.email === userEmail);
    if (!match) return;
    setSalesAgent({ value: match.id, label: `${match.FirstName} ${match.LastName}` });
    if (match.TeamId) setTeam(teamOptions.find(o => o.value === match.TeamId) ?? null);
  }, [isNewQuote, opportunityKey, salesAgent, userEmail, usersList, teamsList, teamOptions]);

  const setFieldValues = (quote_data) => {
    if (quote_data) {
      if (quote_data.ReferringAgentId && quote_data.ReferringAgent) {
        setReferringAgent({
          value: quote_data.ReferringAgentId,
          label: quote_data.ReferringAgent.Name,
        });
      }

      if (quote_data.SalesAgent) {
        setSalesAgent({
          value: quote_data.SalesAgent.id,
          label: `${quote_data.SalesAgent.FirstName} ${quote_data.SalesAgent.LastName}`,
        });
      }

      if (quote_data.TeamId && quote_data.Team) {
        setTeam({
          value: quote_data.TeamId,
          label: quote_data.Team.Name,
        });
      }

      if (quote_data.PartnerId && quote_data.Partner) {
        setPartner({
          value: quote_data.PartnerId,
          label: quote_data.Partner.Name,
        });
      }
    }
  };

  const changeQuoteType1 = dirty(setQuoteType1);
  const changeQuoteContractMonths1 = dirty((value) => setContractMonths1(value));
  const changeQuoteType2 = dirty(setQuoteType2);
  const changeQuoteContractMonths2 = dirty((value) => setContractMonths2(value));

  const onLogoDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    uploadLogo({ variables: { file } }).then(({ data }) => {
      const filename = data.uploadCustomerLogo;
      setLogoFile(Object.assign(file, { preview: getLogoUrl(filename) }));
      setCustomerLogo(filename);
      setIsDirty(true);
    });
  };

  const onLogoClear = () => {
    setLogoFile(null);
    setCustomerLogo(null);
    setIsDirty(true);
  };

  const contractTermsChanged = !isNewQuote && hasQuoteItems && (
    parseInt(contractMonths1) !== existingQuoteData?.Quote?.QuoteContractMonths1 ||
    parseInt(contractMonths2) !== existingQuoteData?.Quote?.QuoteContractMonths2 ||
    (quoteType1 !== '' && parseInt(quoteType1) !== existingQuoteData?.Quote?.QuoteTypeId1) ||
    (quoteType2 !== '' && parseInt(quoteType2) !== existingQuoteData?.Quote?.QuoteTypeId2)
  );

  const doSave = (recalculatePricing) => {
    const defaultValidUntil = new Date(new Date().setDate(new Date().getDate() + 30))
      .toLocaleString('en-ZA')
      .replace(/\//g, '-')
      .replace(/,/g, '');

    const buildQuoteInput = (quoteId) => ({
      QuoteId: quoteId,
      CustomerLogo: customerLogo,
      DisplayCustomerLogo: displayCustomerLogo,
      GroupItemsByCategory: groupItemsByCategory,
      ContactFirstname: contactFirstname,
      ContactSurname: contactSurname,
      ContactEmail: contactEmail,
      PrimaryContact: existingQuoteData?.Quote?.PrimaryContact,
      Description: quoteDescription,
      CompanyName: companyName,
      CustomerId: customerId,
      ProposalTitle: quoteProposalTitle || '',
      ValidUntil: quoteValidUntil || defaultValidUntil,
      QuoteTypeId1: quoteType1 !== '' ? parseInt(quoteType1, 10) : null,
      QuoteTypeId2: quoteType2 !== '' ? parseInt(quoteType2, 10) : null,
      QuoteContractMonths1: parseInt(contractMonths1, 10),
      QuoteContractMonths2: parseInt(contractMonths2, 10),
      WorkflowStep: 'QUOTE_ITEMS',
      ReferringAgentId: referringAgent ? parseInt(referringAgent.value) : null,
      SalesAgentId: salesAgent ? parseInt(salesAgent.value) : null,
      TeamId: team ? team.value : null,
      PartnerId: partner ? parseInt(partner.value) : null,
      RecalculatePricing: recalculatePricing,
      OpportunityTicketKey: opportunityTicketKey,
    });

    if (isNewQuote) {
      const teamId = team ? team.value : null;
      const afterCreate = (QuoteId) => {
        updateQuote({ variables: { QuoteInput: { ...buildQuoteInput(QuoteId), RecalculatePricing: true } } }).then(() => {
          if (unblockRef.current) { unblockRef.current(); unblockRef.current = null; }
          history.replace(`/quote-items/${QuoteId}`);
        });
      };
      if (template) {
        createQuoteFromTemplate({ variables: { input: { QuoteTemplateId: template.value, TeamId: teamId } } })
          .then(({ data }) => afterCreate(data.createQuoteFromTemplate.quote.QuoteId));
      } else {
        createQuote({ variables: { CreateQuoteInput: { TeamId: teamId } } })
          .then(({ data }) => afterCreate(data.createQuote.quote.QuoteId));
      }
    } else {
      updateQuote({
        variables: { QuoteInput: buildQuoteInput(quoteIDParam) },
      }).then(() => {
        if (unblockRef.current) { unblockRef.current(); unblockRef.current = null; }
        history.replace(`/quote-items/${existingQuoteData.Quote.QuoteId}`);
      });
    }
  };

  const proceedNextStep = (args) => {
    //field validation
    if (!quoteDescription) {
      setStepError({
        error: true,
        message: 'Internal description is required',
      });
    } else if (!salesAgent) {
      setStepError({
        error: true,
        message: 'Sales agent is required',
      });
    } else if (!team) {
      setStepError({
        error: true,
        message: 'Team is required',
      });
    } else if (!companyName) {
      setStepError({
        error: true,
        message: 'Company name is required',
      });
    } else if (!contactFirstname) {
      setStepError({
        error: true,
        message: 'Contact first name is required',
      });
    } else if (!contactSurname) {
      setStepError({
        error: true,
        message: 'Contact last name is required',
      });
    } else if (!contactEmail) {
      setStepError({
        error: true,
        message: 'Contact email is required',
      });
    } else if (!quoteProposalTitle) {
      setStepError({
        error: true,
        message: 'Proposal title is required',
      });
    } else if (quoteType1 && contractMonths1 == null) {
      setStepError({
        error: true,
        message: 'Contract period is required for Quote 1',
      });
    } else if (quoteType2 && contractMonths2 == null) {
      setStepError({
        error: true,
        message: 'Contract period is required for Quote 2',
      });
    } else if (contractTermsChanged) {
      setRecalcModal(true);
    } else {
      doSave(false);
    }
  };

  if (!prefillApplied) {
    if (!prefillLoading && !prefillData?.QuotePrefill) {
      return (
        <div className="content">
          <div className="container-fluid">
            <div className="page-title-box">
              <Row className="align-items-center">
                <Col sm="6">
                  <h4 className="page-title">Opportunity {opportunityKey} not found</h4>
                </Col>
              </Row>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  return (
    <React.Fragment>
      {leavePrompt}
      {disableQuote && <div className="overlay"></div>}

      {stepError && (
        <SweetAlert
          error
          title="Error"
          onConfirm={(e) => setStepError(null)}
        >
          {stepError.message}
        </SweetAlert>
      )}

      {recalcModal && (
        <SweetAlert
          warning
          title="Contract Terms Changed"
          onConfirm={() => { setRecalcModal(null); doSave(true); }}
          onCancel={() => { setRecalcModal(null); doSave(false); }}
          showCancel
          confirmBtnText="Recalculate pricing"
          cancelBtnText="Leave unchanged"
          cancelBtnBsStyle="secondary"
        >
          Recalculate quote item pricing and usage rates using the new quote type and contract period?
        </SweetAlert>
      )}

      {(isNewQuote || (existingQuoteData && existingQuoteData.Quote)) && (
        <div className="content">
          <div className="container-fluid">
            <div className="page-title-box">
              <Row className="align-items-center">
                <Col sm="6">
                  <h4 className="page-title">
                    {isNewQuote ? 'New Quote Setup' : `${quoteDescription} - Quote Setup`}
                  </h4>
                </Col>
              </Row>
            </div>

            <Row>
              <Col lg="12">
                <Card>
                  <CardBody>
                    <h4 className="mt-0 header-title">Quote Details</h4>
                    <div>
                      <table className="table">
                        <tbody>
                          {opportunityTicketKey && (
                            <tr>
                              <th scope="row">Opportunity</th>
                              <td>
                                <a href={`https://${process.env.REACT_APP_JIRA_SUBDOMAIN}.atlassian.net/browse/${opportunityTicketKey}`} target="_blank" rel="noreferrer" className="btn-link">
                                  {opportunityTicketKey}
                                </a>
                              </td>
                            </tr>
                          )}
                          {isNewQuote && templatesData?.QuoteTemplates?.length > 0 && (
                          <tr>
                            <th scope="row">Quote template</th>
                            <td>
                              <Select
                                options={(templatesData?.QuoteTemplates || []).map(t => ({ value: t.QuoteId, label: t.ProposalTitle, description: t.Description }))}
                                value={template}
                                onChange={(opt) => {
                                  setTemplate(opt);
                                  if (opt) {
                                    const t = templatesData.QuoteTemplates.find(t => t.QuoteId === opt.value);
                                    if (t) {
                                      setPartner(t.PartnerId ? { value: t.PartnerId, label: t.Partner.Name } : null);
                                      setQuoteType1(t.QuoteTypeId1 != null ? t.QuoteTypeId1 : '');
                                      setQuoteType2(t.QuoteTypeId2 != null ? t.QuoteTypeId2 : '');
                                      setContractMonths1(t.QuoteContractMonths1);
                                      setContractMonths2(t.QuoteContractMonths2);
                                      setGroupItemsByCategory(!!t.GroupItemsByCategory);
                                    }
                                  }
                                }}
                                formatOptionLabel={(opt, { context }) => (
                                  <div style={{ textAlign: 'left' }}>
                                    <div>{opt.label}</div>
                                    {context === 'menu' && opt.description && <div style={{ fontSize: '0.8em', opacity: 0.8 }}>{opt.description}</div>}
                                  </div>
                                )}
                                isClearable
                                placeholder="None"
                              />
                            </td>
                          </tr>
                          )}
                          {!isNewQuote && (
                          <tr>
                            <th scope="row">Quote owner</th>
                            <td>
                              {existingQuoteData?.Quote?.CreatedBy || ''}
                            </td>
                          </tr>
                          )}
                          {!isNewQuote && (
                          <tr>
                            <th scope="row">Quote created</th>
                            <td>
                              {formatTimestamp(existingQuoteData?.Quote?.Created)}
                            </td>
                          </tr>
                          )}
                          <tr>
                            <th scope="row">Internal description <span className="text-danger">*</span></th>
                            <td>
                              <Input
                                type="text"
                                value={quoteDescription}
                                onChange={dirty((e) => setQuoteDescription(e.target.value))}
                              />
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              Sales agent <span className="text-danger">*</span>
                            </th>
                            <td>
                              {Boolean(usersList) ? (
                                <Select
                                  options={usersList.map((u) => ({
                                    value: u.id,
                                    label: `${u.FirstName} ${u.LastName}`,
                                  }))}
                                  value={salesAgent}
                                  placeholder="Please select a sales agent..."
                                  onChange={dirty((opt) => {
                                    setSalesAgent(opt);
                                    if (opt && opt.value !== salesAgent?.value) {
                                      const u = usersList.find(u => u.id === opt.value);
                                      if (u?.TeamId) setTeam(teamOptions.find(o => o.value === u.TeamId));
                                    }
                                  })}
                                />
                              ) : null}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              Team <span className="text-danger">*</span>
                            </th>
                            <td>
                              <Select
                                options={teamOptions}
                                value={team}
                                placeholder="Please select a team..."
                                onChange={dirty(setTeam)}
                              />
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">Referring agent</th>
                            <td>
                              {Boolean(agentsList) ? (
                                <Select
                                  isClearable
                                  options={[
                                    ...agentsList.map((indiv_agent) => ({
                                      value: indiv_agent.AgentId,
                                      label: indiv_agent.Name,
                                    })),
                                  ]}
                                  value={referringAgent}
                                  placeholder="Please select a referring agent..."
                                  onChange={dirty(setReferringAgent)}
                                />
                              ) : null}
                            </td>
                          </tr>
                          {partnerOptions.length > 0 && (
                          <tr>
                            <th scope="row">Partner</th>
                            <td>
                              <Select
                                isClearable
                                options={partnerOptions}
                                value={partner}
                                placeholder="Please select a partner..."
                                onChange={dirty(setPartner)}
                              />
                            </td>
                          </tr>
                          )}
                          <tr>
                            <th scope="row">Valid until</th>
                            <td>
                              <DatePicker
                                className="form-control"
                                dateFormat="MMMM do, yyyy"
                                selected={(() => {
                                  if (quoteValidUntil) return new Date(quoteValidUntil);
                                  const d = new Date();
                                  d.setDate(d.getDate() + 30);
                                  return d;
                                })()}
                                onChange={dirty((e) => {
                                  if (e) {
                                    setQuoteValidUntil(
                                      new Date(e)
                                        .toLocaleString('en-ZA')
                                        .replace(/\//g, '-')
                                        .replace(/,/g, '')
                                    );
                                  } else {
                                    var defaultDate = new Date();
                                    defaultDate.setDate(defaultDate.getDate() + 30);
                                    setQuoteValidUntil(
                                      defaultDate
                                        .toLocaleString('en-ZA')
                                        .replace(/\//g, '-')
                                        .replace(/,/g, '')
                                    );
                                  }
                                })}
                              />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col lg="12">
                <Card>
                  <CardBody id="customer_details_section">
                    <h4 className="mt-0 header-title">Customer Details</h4>
                    <div className="table-responsive">
                      <table className="table">
                        <tbody>
                          <tr>
                            <th scope="row">
                              Company name <span className="text-danger">*</span>
                            </th>
                            <td>
                              <Input
                                type="text"
                                value={companyName || ''}
                                onChange={dirty((e) => setCompanyName(e.target.value))}
                              />
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              Contact first name <span className="text-danger">*</span>
                            </th>
                            <td>
                              <Input
                                type="text"
                                value={contactFirstname || ''}
                                onChange={dirty((e) => setContactFirstname(e.target.value))}
                              />
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              Contact last name <span className="text-danger">*</span>
                            </th>
                            <td>
                              <Input
                                type="text"
                                value={contactSurname || ''}
                                onChange={dirty((e) => setContactSurname(e.target.value))}
                              />
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">
                              Contact email <span className="text-danger">*</span>
                            </th>
                            <td>
                              <Input
                                type="text"
                                value={contactEmail || ''}
                                onChange={dirty((e) => setContactEmail(e.target.value))}
                              />
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col lg="12">
                <Card>
                  <CardBody id="customer_details_section">
                    <h4 className="mt-0 header-title">Proposal Details</h4>
                    <div className="table-responsive">
                      <table className="table">
                        <tbody>
                          <tr>
                            <th scope="row">Proposal title <span className="text-danger">*</span></th>
                            <td>
                              <Input
                                type="text"
                                value={quoteProposalTitle || ''}
                                onChange={dirty((e) => setQuoteProposalTitle(e.target.value))}
                              />
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">Customer logo</th>
                            <td>
                              <ImageDropzone
                                onDrop={onLogoDrop}
                                onClear={onLogoClear}
                                imageUrl={logoFile?.preview ?? (customerLogo ? getLogoUrl(customerLogo) : null)}
                                name="customer logo"
                              />
                              <div className="d-flex align-items-center mt-2">
                                <input
                                  type="checkbox"
                                  id="display-customer-logo"
                                  checked={displayCustomerLogo}
                                  onChange={(e) => { setDisplayCustomerLogo(e.target.checked); setIsDirty(true); }}
                                />
                                <label htmlFor="display-customer-logo" className="ml-2 mb-0">
                                  Display customer logo on cover page
                                </label>
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <th scope="row"></th>
                            <td>
                              <div className="d-flex align-items-center">
                                <input
                                  type="checkbox"
                                  id="group-items-by-category"
                                  checked={groupItemsByCategory}
                                  onChange={(e) => { setGroupItemsByCategory(e.target.checked); setIsDirty(true); }}
                                />
                                <label htmlFor="group-items-by-category" className="ml-2 mb-0">
                                  Group quote items by category on proposal pricing tables
                                </label>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <QuoteSettingsPanel
              title="Quote 1 Settings"
              quoteTypes={quoteTypes}
              quoteTypeValue={quoteType1}
              contractMonths={contractMonths1}
              onQuoteTypeChange={changeQuoteType1}
              onContractMonthsChange={changeQuoteContractMonths1}
              otherQuoteTypeValue={quoteType2}
            />

            <QuoteSettingsPanel
              title="Quote 2 Settings"
              quoteTypes={quoteTypes}
              quoteTypeValue={quoteType2}
              contractMonths={contractMonths2}
              onQuoteTypeChange={changeQuoteType2}
              onContractMonthsChange={changeQuoteContractMonths2}
              otherQuoteTypeValue={quoteType1}
            />

            <Row>
              <Col lg="12">
                <Card align="right">
                  <CardBody>
                    <button
                      className="btn-icon btn btn-primary btn-lg"
                      onClick={proceedNextStep}
                    >
                      {' '}
                      <span className="btn-icon-label">
                        <i className="ion ion-md-arrow-forward mr-2"></i>
                      </span>{' '}
                      Next Step
                    </button>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default connect(null, { activateAuthLayout })(QuoteBuilderSetup);
